import clsx from "clsx";
import React from "react";
import HoverArrowLink from "../HoverArrowLink";
import CustomLink from "../CustomLink";
import SingleContinentMenuItem from "./SingleContinentMenuItem";
import { LocationData } from "~/slices/LocationsSection";
import { groupBy, map } from "lodash";
import { Continent, LocationType } from "./MapWrapper";

const types = [
  { title: "Facilities", type: "Facility" },
  { title: "Offices", type: "Office" },
];

function FilteredLocations({
  name,
  title,
  locations,
  isLocationOverview,
  open,
  handleLocationClick,
}: {
  name: string;
  title: string;
  locations?: LocationData[];
  isLocationOverview: boolean;
  open: boolean;
  handleLocationClick: (name: LocationType) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <button
        className={clsx("relative w-fit cursor-pointer transition-opacity")}
        onClick={() => handleLocationClick(name as LocationType)}
        name={name}
      >
        <h2
          className={clsx(
            "text-heading-6",
            open ? "text-white" : "text-white/60",
          )}
        >
          {title}
        </h2>
      </button>
      {Array.isArray(locations) && locations.length > 0 ? (
        <ul
          className={`${
            open ? "items-visible" : "items-hidden"
          } mb-2 mt-2 hidden justify-start gap-2 md:grid-cols-2 md:gap-x-8 md:gap-y-1 lg:grid lg:grid-cols-1`}
        >
          {locations.map((location, i) => {
            if (!location.name && !location.country) return null;
            return (
              <CustomLink
                key={i}
                to={location?.link}
                className="group opacity-60 transition-opacity hover:opacity-100"
              >
                <span className="flex items-start gap-2 text-left text-heading-6 text-white">
                  <span className="translate-z-0">
                    <HoverArrowLink
                      className="w-5 shrink-0 translate-y-0.5"
                      arrowClass="stroke-white"
                      direction="right"
                    />
                  </span>
                  <span>
                    {location.name}
                    {location.name && location.country && ", "}
                    {location.country}
                  </span>
                </span>
              </CustomLink>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default function SubMenuLocations({
  locations = [],
  activeContinent,
  updateContinentHandler,
  activeType,
  handleTypeClick,
  isLocationOverview = false,
}: {
  locations?: LocationData[];
  activeContinent: string;
  updateContinentHandler: (continent: Continent) => void;
  activeType: string | null;
  handleTypeClick: (type: LocationType) => void;
  isLocationOverview: boolean;
}) {
  return (
    <div
      className={clsx(
        "relative gap-2 text-white transition duration-scalingCursor md:col-span-8 md:items-start lg:col-span-3 lg:gap-5",
        isLocationOverview
          ? "col-span-4 flex flex-col px-5 md:px-0"
          : "hidden md:flex md:flex-col",
      )}
    >
      {Array.isArray(locations) && locations.length > 0 && (
        <div className="flex flex-col gap-2 md:flex-row lg:flex-col lg:items-start">
          <div className="-mb-1 shrink-0 text-tiny-body text-slate-grey">
            View by type
          </div>
          {types.map((type) => (
            <FilteredLocations
              key={type.title}
              name={type.title}
              title={type.title}
              locations={locations.filter(
                (location) =>
                  location.type?.[0]?.toLowerCase() === type.type.toLowerCase(),
              )}
              isLocationOverview={isLocationOverview}
              open={activeType === type.title}
              handleLocationClick={handleTypeClick}
            />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-2 md:flex-row lg:flex-col lg:items-start">
        <div className="text-tiny-body text-slate-grey">View by location</div>
        {locations &&
          locations.length > 0 &&
          map(
            groupBy(locations, (location) => location.continent),
            (continentLocations, continentName) => {
              return (
                <SingleContinentMenuItem
                  activeContinent={activeContinent}
                  continentName={continentName}
                  href="/facilities"
                  updateContinentHandler={updateContinentHandler}
                  description={""}
                  key={continentName}
                  isLocationOverview={isLocationOverview}
                  locations={continentLocations}
                />
              );
            },
          )}
      </div>
    </div>
  );
}
